import type { Chapter } from "../api";
import type { ReaderMode } from "./readerSettings";

/**
 * Auto-play next chapter (requirement 0110, ADR-0177). The app keeps the same
 * rule (mobile/lib/player/auto_play.dart); both copies are held to
 * testdata/player/auto-play.json — change the fixture first.
 */

/** How long the Next button fills before the next chapter opens. */
export const AUTO_PLAY_COUNTDOWN_MS = 45_000;

/**
 * What a chapter's audio ending leads to. The web never goes on unseen: a
 * hidden tab does nothing (ADR-0177, Web is unchanged), so its answer is
 * `nothing` or `countdown`.
 */
export type AutoPlayStep = "nothing" | "countdown" | "go";

/** Whether a chapter the list carries has anything to play. */
export function hasAudio(chapter: Pick<Chapter, "duration_ms"> | null | undefined): boolean {
  return (chapter?.duration_ms ?? 0) > 0;
}

/**
 * atChapterEnd is the one rule. `inFront` is the tab being visible: a
 * countdown nobody can see is not offered, and the web does not move on in a
 * hidden tab.
 */
export function atChapterEnd(o: {
  setting: boolean;
  mode: ReaderMode;
  next: Pick<Chapter, "duration_ms"> | null | undefined;
  inFront: boolean;
}): AutoPlayStep {
  if (!o.setting || o.mode !== "listen" || !hasAudio(o.next) || !o.inFront) return "nothing";
  return "countdown";
}

/** What can happen while the Next button fills. */
export type AutoPlayEvent =
  "elapsed" | "next" | "play" | "seek" | "setting_off" | "leave" | "hidden";

/** Whether an event during a countdown goes on now; anything else cancels it. */
export function goesOn(e: AutoPlayEvent): boolean {
  return e === "elapsed" || e === "next";
}
